import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
} from "@colorglo/ui/components";
import { useId, useState } from "react";
import { toast } from "sonner";
import { useChangePassword } from "../api/auth.ts";
import { useT } from "../lib/locale.ts";

const MIN_LENGTH = 12;

export function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  const change = useChangePassword();
  const currentId = useId();
  const nextId = useId();
  const confirmId = useId();
  const hintId = useId();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const mismatch = confirm !== "" && next !== confirm;
  const canSubmit =
    current !== "" && next.length >= MIN_LENGTH && next === confirm && !change.isPending;

  const close = (value: boolean) => {
    if (!value) {
      setCurrent("");
      setNext("");
      setConfirm("");
      change.reset();
    }
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <form
          className="flex flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            change.mutate(
              { currentPassword: current, newPassword: next },
              {
                onSuccess: () => {
                  toast.success(t("auth.change_password.done"));
                  close(false);
                },
              },
            );
          }}
        >
          <DialogHeader>
            <DialogTitle>{t("auth.change_password.title")}</DialogTitle>
            <DialogDescription>{t("auth.change_password.body")}</DialogDescription>
          </DialogHeader>

          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={currentId}>{t("auth.change_password.current")}</FieldLabel>
              <Input
                id={currentId}
                type="password"
                autoComplete="current-password"
                required
                aria-invalid={change.isError || undefined}
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
              />
              {change.isError && <FieldError>{t("auth.change_password.failed")}</FieldError>}
            </Field>

            <Field>
              <FieldLabel htmlFor={nextId}>{t("auth.change_password.new")}</FieldLabel>
              <Input
                id={nextId}
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_LENGTH}
                aria-describedby={hintId}
                value={next}
                onChange={(event) => setNext(event.target.value)}
              />
              <FieldDescription id={hintId}>{t("invite.password_hint")}</FieldDescription>
            </Field>

            <Field>
              <FieldLabel htmlFor={confirmId}>{t("auth.change_password.confirm")}</FieldLabel>
              <Input
                id={confirmId}
                type="password"
                autoComplete="new-password"
                required
                aria-invalid={mismatch || undefined}
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
              {mismatch && <FieldError>{t("auth.change_password.mismatch")}</FieldError>}
            </Field>
          </FieldGroup>

          <DialogFooter>
            <Button type="submit" disabled={!canSubmit}>
              {t("auth.change_password.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
